import { createContext, useContext, useMemo, useSyncExternalStore } from 'react'
import { ancestorsOf, formatMoney, rowFieldKeys, rowSubtitle, subtreeSum, todayIn, type HouseholdSettings, type ItemSnapshot, type Tint, type UserSettings } from '@terrazzo/core'
import { store } from './store'
import { me } from './me'
import { sync, type SyncStatus } from '../sync/doc'
import { deviceZone } from '../lib/clock'

export function useItem(id: string | null | undefined): ItemSnapshot | undefined {
  return useSyncExternalStore(
    (cb) => (id ? store.subscribeItem(id, cb) : () => {}),
    () => (id ? store.get(id) : undefined),
  )
}

/** Re-render on structural changes (moves, checks, dates, archive/trash…). */
export function useStructure(): number {
  return useSyncExternalStore(store.subscribeStructure, () => store.structureVersion)
}

/** Re-render on any item change (titles/bodies too). Use sparingly. */
export function useContent(): number {
  return useSyncExternalStore(store.subscribeContent, () => store.contentVersion)
}

const noSubscribe = () => () => {}
/** useContent, but only while `active` (e.g. while a find is open); 0 otherwise. */
export function useContentWhile(active: boolean): number {
  return useSyncExternalStore(active ? store.subscribeContent : noSubscribe, () => (active ? store.contentVersion : 0))
}

export function useMetaVersion(): number {
  return useSyncExternalStore(store.subscribeMeta, () => store.metaVersion)
}

// Settings-type data is read as the external store's snapshot (cached per meta version),
// never as "subscribe, then call a getter": the React Compiler memoizes a plain call like
// store.household() and would keep returning the first value after a change.
export function useHousehold(): HouseholdSettings {
  return useSyncExternalStore(store.subscribeMeta, () => store.household())
}

export function useUsers() {
  return useSyncExternalStore(store.subscribeMeta, () => store.users())
}

export function useFields() {
  return useSyncExternalStore(store.subscribeMeta, () => store.fields())
}

export function useMe() {
  return useSyncExternalStore(me.subscribe, me.get)
}

export function useMySettings(): UserSettings & Record<string, unknown> {
  const id = useMe().user?.id ?? ''
  return useSyncExternalStore(store.subscribeMeta, () => store.userSettings(id))
}

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(sync.subscribe, sync.getStatus)
}

let todayCache = { tz: '', date: '', at: 0 }
function computeToday(tz: string) {
  const now = Date.now()
  if (todayCache.tz !== tz || now - todayCache.at > 30_000) todayCache = { tz, date: todayIn(tz, now), at: now }
  return todayCache.date
}
const todaySubs = new Set<() => void>()
if (typeof window !== 'undefined') {
  setInterval(() => {
    const before = todayCache.date
    todayCache.at = 0
    if (computeToday(deviceZone()) !== before) for (const f of todaySubs) f()
  }, 60_000)
  document.addEventListener('visibilitychange', () => {
    todayCache.at = 0
    for (const f of todaySubs) f()
  })
}

/** Today where this phone is (dates are calendar days; see lib/clock), updates across midnight. */
export function useToday(): string {
  const tz = deviceZone()
  return useSyncExternalStore(
    (cb) => {
      todaySubs.add(cb)
      return () => todaySubs.delete(cb)
    },
    () => computeToday(tz),
  )
}

/** Memo helper keyed on the structure version. */
export function useDerived<T>(fn: () => T, deps: unknown[]): T {
  const v = useStructure()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(fn, [v, ...deps])
}

export function currentToday(): string {
  return computeToday(deviceZone())
}

/** The tab a card or row is shown in (its id), with the subtitles it sets for its cards. */
export interface TabInfo {
  view: string
  subtitles?: Record<string, string[]>
  /** Its group cards' colors (by group key). */
  tints?: Record<string, Tint>
}
export const TabCtx = createContext<TabInfo | null>(null)
export const useTab = () => useContext(TabCtx)

const NO_BITS: string[] = []
export interface RowSubtitle {
  /** Field and tag bits ("$450", "Waikiki", "Total $1,240"). */
  bits: string[]
  date: boolean
  person: boolean
  repeat: boolean
}
/** A row's subtitle (the small line under it), as its card — or the tab, for that card — sets it; kept current. */
export function useRowSubtitle(id: string): RowSubtitle {
  const overrides = useTab()?.subtitles
  const s = useSyncExternalStore(store.subscribeContent, () => {
    const r = rowSubtitle(store.idx, id, store.fields(), overrides)
    return `${+r.date}${+r.person}${+r.repeat}${r.bits.join('\u0001')}`
  })
  return useMemo(() => ({ date: s[0] === '1', person: s[1] === '1', repeat: s[2] === '1', bits: s.length > 3 ? s.slice(3).split('\u0001') : NO_BITS }), [s])
}

/** A card's money total(s) for its header, for the money fields it shows ("$4,200"). */
export function useSubtreeTotal(id: string): string {
  const overrides = useTab()?.subtitles
  return useSyncExternalStore(store.subscribeContent, () => {
    const idx = store.idx
    const reg = store.fields()
    const it = idx.items.get(id)
    if (!it) return ''
    const chain = [
      it,
      ...ancestorsOf(idx.tree, id)
        .map((a) => idx.items.get(a)!)
        .filter(Boolean),
    ]
    return rowFieldKeys(chain, reg, overrides)
      .filter((k) => reg.get(k)?.type === 'money')
      .map((k) => {
        const sum = subtreeSum(idx, id, k)
        return sum ? formatMoney(sum) : ''
      })
      .filter(Boolean)
      .join(' · ')
  })
}
